import SettingsIcon from "@mui/icons-material/Settings";
import React from "react";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { shallowEqual, useSelector } from "react-redux";
import { IRootState } from "../../../../../modules";
import { getManagedSecurityGroups } from "../../../../../functions/securityGroupHelper";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import { standardsManagementAdminRoles } from "@features/ecommerce/hooks/standardsAuthorization";
import { hasTenantId } from "../../../components/TenantRouteGuard";

export default function useGetSettingsNavItems() {
  const {
    hasRoleIn,
    getClaimValue,
    hasPermission,
    getPermissionResourceIdentifiers,
  } = useClaims();
  const { subDomain } = useSelector(
    (state: IRootState) => state.site.labelInfo,
  );

  const isAdmin = hasRoleIn("CustomerAdmin", "ShiAdmin");
  const tenantId = getClaimValue("tenantId");
  const hasTenant = hasTenantId(tenantId);
  const isStorefrontsAdmin = hasRoleIn(...standardsManagementAdminRoles);

  const canManageStorefronts =
    isStorefrontsAdmin ||
    hasPermission(ShiOnePermission.storefrontManage) ||
    getPermissionResourceIdentifiers(ShiOnePermission.storefrontManage).length >
      0;

  const showStorefronts = canManageStorefronts;

  const myTeams = useSelector(
    (state: IRootState) => state.teamRtk.myTeams,
    shallowEqual,
  );

  const userId = getClaimValue("userId");
  const myManagedSecurityGroups = getManagedSecurityGroups(
    userId,
    myTeams as Parameters<typeof getManagedSecurityGroups>[1],
  );

  const showSettingsSection =
    isAdmin || myManagedSecurityGroups?.length > 0 || showStorefronts;

  const accountId = getClaimValue("accountId");

  let shouldDisplayAdmin = true;
  if (subDomain === "lpl" && accountId !== "175") {
    shouldDisplayAdmin = false;
  }

  return [
    {
      id: "settings",
      type: "Group",
      displayName: "Settings",
      visible: showSettingsSection && shouldDisplayAdmin,
      icon: <SettingsIcon />,
      children: [
        {
          id: "settings-general",
          type: "Link",
          displayName: "General",
          visible: isAdmin,
          path: "/settings/general",
        },
        {
          id: "settings-integrations",
          type: "Link",
          displayName: "Integrations",
          visible: isAdmin,
          path: "/settings/integrations",
        },
        {
          id: "settings-user-management",
          type: "Link",
          displayName: "User Management",
          visible: hasTenant && isAdmin,
          path: "/settings/user-management",
        },
        {
          id: "settings-security-groups",
          type: "Link",
          displayName: "Security Groups",
          visible: hasTenant && (isAdmin || myManagedSecurityGroups.length > 0),
          path: "/settings/team-management",
        },
        {
          id: "settings-storefronts",
          type: "Link",
          displayName: "Storefronts",
          visible: showStorefronts,
          path: "/settings/storefronts",
        },
      ],
    },
  ];
}
